import React, { useState, useRef, useEffect } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { TbSun, TbMoon, TbLogout, TbShieldCheck } from "react-icons/tb";
import { useAuth } from "../../contexts/AuthContext";
import { useTheme } from "../../contexts/ThemeContext";
import { useLanguage } from "../../contexts/LanguageContext";
import { Lockup } from "../Lockup";
import { AccentDot } from "../AccentDot";
import { cn } from "../../lib/utils";

const NAV_ITEMS = [
  { to: "/league", labelKey: "liga" as const },
  { to: "/groups", labelKey: "gus" as const },
  { to: "/people", labelKey: "pessoas" as const },
  { to: "/achievements", labelKey: "conquistas" as const },
  { to: "/rules", labelKey: "rules" as const },
];

export const Header: React.FC = () => {
  const { user, signOut } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { language, setLanguage, t } = useLanguage();
  const location = useLocation();

  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    if (menuOpen) document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [menuOpen]);

  if (location.pathname === "/login") return null;

  const initial = user?.name?.[0]?.toUpperCase() ?? "?";
  const shortName = user?.name
    ? user.name.split(" ").slice(0, 2).join(" ").replace(/\s+(\w)\w*$/, " $1.")
    : "User";

  return (
    <header className="sticky top-0 z-50 bg-white/85 dark:bg-[rgba(10,12,31,0.85)] backdrop-blur-xl border-b border-black/[0.06] dark:border-white/[0.06]">
      <div className="max-w-[1440px] mx-auto px-4 sm:px-8 py-3.5 flex items-center gap-4 sm:gap-8">
        <NavLink to="/league" className="flex items-center gap-3 flex-shrink-0">
          <Lockup size="sm" />
          <span className="text-[13px] font-extrabold tracking-[0.04em] text-foreground dark:text-white hidden xs:inline">
            AGENTIC LEAGUE<AccentDot />
          </span>
        </NavLink>

        <nav className="hidden md:flex gap-1.5">
          {NAV_ITEMS.map(({ to, labelKey }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                cn(
                  "px-3.5 py-2 rounded-lg text-[13px] font-semibold transition-colors",
                  isActive
                    ? "bg-coral/[0.15] text-coral dark:text-coral-soft"
                    : "text-foreground/60 dark:text-white/60 hover:text-foreground dark:hover:text-white",
                )
              }
            >
              {t.nav[labelKey]}
            </NavLink>
          ))}
        </nav>

        <div className="ml-auto flex items-center gap-2 sm:gap-3">
          <button
            onClick={() => setLanguage(language === "pt-BR" ? "en" : "pt-BR")}
            className="text-[11px] font-bold tracking-wider px-2.5 py-1 rounded-full bg-black/[0.04] dark:bg-white/[0.04] border border-black/10 dark:border-white/10 text-foreground/70 dark:text-white/70 hover:text-foreground dark:hover:text-white hover:bg-black/[0.06] dark:hover:bg-white/[0.08] transition-colors"
            title="PT / EN"
          >
            {language === "pt-BR" ? "EN" : "PT"}
          </button>

          <button
            onClick={toggleTheme}
            className="p-1.5 rounded-full bg-black/[0.04] dark:bg-white/[0.04] border border-black/10 dark:border-white/10 text-foreground/70 dark:text-white/70 hover:text-foreground dark:hover:text-white hover:bg-black/[0.06] dark:hover:bg-white/[0.08] transition-colors"
            aria-label={t.header.toggleTheme}
          >
            {theme === "dark" ? (
              <TbSun className="w-3.5 h-3.5" />
            ) : (
              <TbMoon className="w-3.5 h-3.5" />
            )}
          </button>

          <div ref={menuRef} className="relative">
            <button
              onClick={() => setMenuOpen((o) => !o)}
              className="flex items-center gap-2 pl-1 pr-3 py-1 bg-black/[0.04] dark:bg-white/[0.04] border border-black/10 dark:border-white/10 rounded-full text-[12px] font-semibold text-foreground/90 dark:text-white/90 hover:bg-black/[0.06] dark:hover:bg-white/[0.08] transition-colors"
            >
              {user?.avatar ? (
                <img
                  src={user.avatar}
                  alt={user.name}
                  className="w-6 h-6 rounded-full"
                />
              ) : (
                <div className="w-6 h-6 rounded-full bg-gradient-to-br from-[#FF5C4E] to-[#E8A8E4] flex items-center justify-center text-[10px] font-bold text-white">
                  {initial}
                </div>
              )}
              <span className="hidden sm:inline">{shortName}</span>
            </button>

            {menuOpen && (
              <div className="absolute right-0 mt-2 w-48 rounded-xl bg-white dark:bg-[#0E1138] border border-black/[0.08] dark:border-white/10 shadow-2xl overflow-hidden">
                <nav className="flex flex-col md:hidden border-b border-black/[0.06] dark:border-white/[0.06] py-1">
                  {NAV_ITEMS.map(({ to, labelKey }) => (
                    <NavLink
                      key={to}
                      to={to}
                      onClick={() => setMenuOpen(false)}
                      className={({ isActive }) =>
                        cn(
                          "px-4 py-2 text-[13px] font-semibold transition-colors",
                          isActive
                            ? "text-coral dark:text-coral-soft bg-coral/[0.12]"
                            : "text-foreground/70 dark:text-white/70 hover:text-foreground dark:hover:text-white hover:bg-black/[0.04] dark:hover:bg-white/[0.04]",
                        )
                      }
                    >
                      {t.nav[labelKey]}
                    </NavLink>
                  ))}
                </nav>
                {user?.isAdmin && (
                  <NavLink
                    to="/admin"
                    onClick={() => setMenuOpen(false)}
                    className="flex items-center gap-2 px-4 py-2.5 text-[12px] font-semibold text-foreground/70 dark:text-white/70 hover:text-foreground dark:hover:text-white hover:bg-black/[0.04] dark:hover:bg-white/[0.04] transition-colors"
                  >
                    <TbShieldCheck className="w-3.5 h-3.5" />
                    {t.nav.admin}
                  </NavLink>
                )}
                <button
                  onClick={() => {
                    setMenuOpen(false);
                    signOut();
                  }}
                  className="w-full flex items-center gap-2 px-4 py-2.5 text-[12px] font-semibold text-foreground/70 dark:text-white/70 hover:text-foreground dark:hover:text-white hover:bg-black/[0.04] dark:hover:bg-white/[0.04] transition-colors text-left"
                >
                  <TbLogout className="w-3.5 h-3.5" />
                  {t.header.signOut}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};
